"use client";

import { useState } from "react";
import Link from "next/link";
import { AlertCircle, Mail, Loader2, ArrowLeft, Clock } from "lucide-react";
import { forgotPassword } from "@/lib/api";
import { CrewAheadApiError } from "@/lib/api/client";

// ─── Component ───────────────────────────────────────────────────────────────

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "throttled">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ─── Form Submission ──────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setErrorMessage("");

    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setStatus("loading");

    try {
      await forgotPassword({ email });
      setStatus("success");
    } catch (err) {
      if (err instanceof CrewAheadApiError) {
        if (err.statusCode === 429) {
          setStatus("throttled");
        } else {
          setStatus("error");
          setErrorMessage("Something went wrong on our end. Try again shortly.");
        }
        } else {
          setStatus("error");
          setErrorMessage("Something went wrong on our end. Try again shortly.");
        }
    }
  };

  // ─── Success State ────────────────────────────────────────────────────────

  if (status === "success") {
    return (
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-neon-green/20 bg-neon-green/10">
          <Mail className="h-8 w-8 text-neon-green" aria-hidden="true" />
        </div>
        <h2 className="mt-5 text-2xl font-bold text-foreground">
          Reset link sent
        </h2>
        <p className="mt-2 text-sm text-ash">
          If an account exists for that address, instructions are on their way. Check your inbox and spam folder. The link expires in an hour.
        </p>
        <button
          onClick={() => {
            setEmail("");
            setStatus("idle");
          }}
          className="mt-6 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
        >
          Try again with a different email
        </button>
        <div className="mt-4">
          <Link
            href="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ash hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  // ─── Throttled State ───────────────────────────────────────────────────────

  if (status === "throttled") {
    return (
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-500/20 bg-amber-500/10">
          <Clock className="h-8 w-8 text-amber-500" aria-hidden="true" />
        </div>
        <h2 className="mt-5 text-2xl font-bold text-foreground">
          Too many requests
        </h2>
        <p className="mt-2 text-sm text-ash">
           Too many requests from this connection. Try again later. Nothing is wrong with the address.
        </p>
        <div className="mt-4">
          <Link
            href="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ash hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  // ─── Form State ───────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ash hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to login
        </Link>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-neon-cyan">
          Account Recovery
        </p>
        <h1 className="mt-1 text-2xl font-bold text-foreground">
          Reset your CrewAhead access
        </h1>
        <p className="mt-2 text-sm text-ash">
          Enter the email linked to your CrewAhead account. If it matches an
          account, we will send password reset instructions.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-busy={status === "loading"}
      >
        {/* Email input */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-foreground"
          >
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            autoComplete="email"
            placeholder="you@example.com"
            className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-4 py-2.5 text-sm text-white placeholder:text-muted transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {emailError && (
            <p
              className="mt-1.5 flex items-center gap-1.5 text-xs text-red-400"
              aria-live="polite"
            >
              <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
              {emailError}
            </p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={status === "loading"}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-obsidian transition-all hover:bg-primary/90 disabled:opacity-60"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Sending...
            </>
          ) : (
            "Send reset link"
          )}
        </button>

        {/* Error state */}
        {status === "error" && (
          <div
            className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-400"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span>{errorMessage}</span>
          </div>
        )}
      </form>

      {/* Security note */}
      <p className="mt-4 text-xs text-muted">
        For account safety, never share password reset links with anyone.
      </p>
    </div>
  );
}
